import React, { useEffect, useState } from 'react';
import CryptoJS from 'crypto-js';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const ENCRYPTION_KEY = CryptoJS.enc.Utf8.parse('LxQY43Nc&87x8nxw');
const ENCRYPTION_IV = CryptoJS.enc.Utf8.parse('@@@@&&&&####$$$$');

const processData = (rawData) => {
  if (!rawData) return null;

  try {
    let decoded = rawData;
    while (decoded.includes('%')) {
      decoded = decodeURIComponent(decoded);
    }

    if (decoded.trim().startsWith('[') || decoded.trim().startsWith('{')) {
      return decoded;
    }

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: CryptoJS.enc.Base64.parse(decoded) },
      ENCRYPTION_KEY,
      { iv: ENCRYPTION_IV, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    const result = decrypted.toString(CryptoJS.enc.Utf8);
    return result || null;
  } catch (error) {
    console.error('Processing Error:', error);
    return null;
  }
};

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [decryptedMsg, setDecryptedMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState({
    payment_type: 'Checking...',
    leadId: 'N/A',
    loanNo: 'N/A',
    loanAmount: '0.00',
    transactionDateTime: '',
  });

  const handleReloan = () => {
    const confirmAction = window.confirm('Are you sure you want to proceed for a Re-loan?');

    if (confirmAction) {
      navigate('/apply-now');
    }
  };

  useEffect(() => {
    const handlePaymentResponse = () => {
      try {
        const params = new URLSearchParams(location.search);
        const responseParam = params.get('response');
        const respCodeParam = params.get('respcode');

        const now = new Date();
        const formattedDateTime = now
          .toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          })
          .replace(',', '');

        let finalDetails = {
          payment_type: 'PayTM',
          transactionDateTime: formattedDateTime,
          leadId: 'N/A',
          loanNo: 'N/A',
          loanAmount: '0.00',
        };

        let currentStatus = false;

        if (respCodeParam) {
          const processedResult = processData(respCodeParam);

          if (processedResult) {
            try {
              const jsonStartIndex =
                processedResult.indexOf('[') !== -1
                  ? processedResult.indexOf('[')
                  : processedResult.indexOf('{');
              const cleanJson =
                jsonStartIndex !== -1
                  ? processedResult.substring(jsonStartIndex)
                  : processedResult;

              const parsed = JSON.parse(cleanJson);
              const data = Array.isArray(parsed) ? parsed[0] : parsed;

              if (data.GATEWAYNAME || data.BANKNAME || data.mid) {
                finalDetails.payment_type = 'PayTM';
              } else if (data.key || data.mihpayid || data.mode) {
                finalDetails.payment_type = 'PayU';
              } else {
                finalDetails.payment_type = respCodeParam.includes('%') ? 'PayU' : 'PayTM';
              }

              currentStatus =
                data.response_status === 'TXN_SUCCESS' ||
                data.status === 'success' ||
                data.response_status === 'success';

              finalDetails = {
                ...finalDetails,
                leadId: data.lead_id || data.leadId || 'N/A',
                loanNo: data.loan_no || data.loanNo || 'N/A',
                loanAmount: data.loan_amount || data.loanAmount || '0.00',
              };
            } catch (error) {
              console.error('JSON Error:', error);
            }
          }
        }

        if (responseParam) {
          const msgResult = processData(responseParam);
          if (msgResult) {
            const msg = msgResult.includes('RESPMSG=')
              ? msgResult.split('RESPMSG=')[1].split('|')[0]
              : msgResult;
            setDecryptedMsg(msg);
          }
        }

        setIsSuccess(currentStatus);
        setPaymentDetails(finalDetails);
      } catch (error) {
        console.error('Payment response error:', error);
        setDecryptedMsg('Error loading payment details.');
      } finally {
        setLoading(false);
      }
    };

    handlePaymentResponse();
  }, [location]);

  if (loading) {
    return <div style={styles.container}>Loading...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {isSuccess ? (
          <>
            <FaCheckCircle style={styles.iconSuccess} />
            <h2 style={styles.heading}>Payment Successful</h2>
            <p style={styles.text}>
              Great news! Your payment via <b>{paymentDetails.payment_type}</b> has been successfully processed.
            </p>
          </>
        ) : (
          <>
            <FaTimesCircle style={styles.iconFailure} />
            <h2 style={styles.heading}>Payment Failed</h2>
            <p style={{ ...styles.text, color: '#d9534f' }}>
              We couldn&apos;t process your <b>{paymentDetails.payment_type}</b> payment:{' '}
              {decryptedMsg || 'Transaction Declined'}
            </p>
          </>
        )}

        <div style={styles.detailsBox}>
          <div style={styles.detailRow}>
            <span>Lead ID:</span>
            <strong>{paymentDetails.leadId}</strong>
          </div>
          <div style={styles.detailRow}>
            <span>Loan No:</span>
            <strong>{paymentDetails.loanNo}</strong>
          </div>
          <div style={styles.detailRow}>
            <span>Amount:</span>
            <strong>₹{paymentDetails.loanAmount}</strong>
          </div>
          <div style={styles.detailRow}>
            <span>Date &amp; Time:</span>
            <strong>{paymentDetails.transactionDateTime}</strong>
          </div>
        </div>

        <div style={styles.divider} />

        {isSuccess ? (
          <button type="button" style={styles.buttonReloan} onClick={handleReloan}>
            Click Here for Reloan
          </button>
        ) : (
          <button type="button" style={styles.buttonHome} onClick={() => navigate('/')}>
            Go to Home
          </button>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '85vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f4f7fb',
    padding: '20px',
  },
  card: {
    backgroundColor: '#fff',
    padding: '40px',
    borderRadius: '16px',
    textAlign: 'center',
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
    maxWidth: '480px',
    width: '100%',
  },
  iconSuccess: {
    fontSize: '70px',
    color: '#28a745',
    marginBottom: '20px',
  },
  iconFailure: {
    fontSize: '70px',
    color: '#dc3545',
    marginBottom: '20px',
  },
  heading: {
    fontSize: '26px',
    fontWeight: '700',
    marginBottom: '15px',
    color: '#333',
  },
  text: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#555',
    marginBottom: '20px',
  },
  detailsBox: {
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '12px',
    marginTop: '10px',
    textAlign: 'left',
    border: '1px solid #eee',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    fontSize: '15px',
    color: '#444',
  },
  divider: {
    height: '1px',
    backgroundColor: '#eee',
    margin: '25px 0',
  },
  buttonReloan: {
    padding: '14px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'rgb(5, 48, 37)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
    width: '100%',
    fontWeight: '600',
    transition: '0.3s',
  },
  buttonHome: {
    padding: '14px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'rgb(5, 48, 37)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
    width: '100%',
    fontWeight: '600',
  },
};

export default PaymentSuccess;
